import { RESPONSE_STATUSES } from "@/api/constants";
import { APP_CONFIG } from "@/config/app";
import { publicRoutes } from "@/config/routes/publicRoutes";
import {
  isRefreshEndpoint,
  refreshTokens,
} from "@/services/auth/authTokenRefresh";
import { rootStore } from "@/store/RootStore";
import type { ApiError } from "@/types/api";

class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = APP_CONFIG.apiUrl;
  }

  logout() {
    rootStore.authStore.logout();
  }

  private async fetchWithAuth(
    endpoint: string,
    options: RequestInit = {},
    publicRoute: boolean = false,
    isRetry: boolean = false
  ): Promise<Response> {
    const url = `${this.baseUrl}${endpoint}`;

    const token = rootStore.authStore.token;
    const isFormData = options.body instanceof FormData;

    const mergedHeaders: Record<string, string> = {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      mergedHeaders.Authorization = `Bearer ${token}`;
    }

    const allowedRoutes = publicRoutes.map(route => route.path);
    if (!publicRoute && !token && !allowedRoutes.includes(location.pathname)) {
      this.logout();
      throw new Error("Unauthorized");
    }

    const response = await fetch(url, {
      ...options,
      headers: mergedHeaders,
    });

    if (
      response.status === RESPONSE_STATUSES.UNAUTHORIZED &&
      token &&
      !publicRoute &&
      !isRefreshEndpoint(endpoint) &&
      !isRetry
    ) {
      try {
        await refreshTokens();
      } catch {
        throw new Error("Unauthorized");
      }

      return this.fetchWithAuth(endpoint, options, publicRoute, true);
    }

    if (
      response.status === RESPONSE_STATUSES.UNAUTHORIZED &&
      token &&
      !publicRoute &&
      isRetry
    ) {
      this.logout();
    }

    return response;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    publicRoute: boolean = false
  ): Promise<T> {
    try {
      const response = await this.fetchWithAuth(endpoint, options, publicRoute);

      const responseText = await response.text();

      let data;
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        data = { message: responseText };
      }

      if (!response.ok) {
        const errorPayload =
          typeof data === "object" && data !== null
            ? data
            : { message: String(data) };

        throw {
          ...errorPayload,
          status:
            errorPayload.status ?? errorPayload.statusCode ?? response.status,
        };
      }
      return data;
    } catch (error: unknown) {
      const payload =
        typeof error === "object" && error !== null
          ? (error as {
              message?: string;
              description?: string;
              status?: number;
              statusCode?: number;
              code?: string | number;
              property?: string;
              extra?: Record<string, unknown>;
              errors?: ApiError["errors"];
            })
          : {};

      const apiError: ApiError = {
        message:
          payload.description ??
          payload.message ??
          (error instanceof Error ? error.message : "Unknown error"),
        status: Number(payload.status ?? payload.statusCode ?? 0),
        code:
          payload.code != null
            ? String(payload.code)
            : RESPONSE_STATUSES.DEFAULT_ERROR,
        property: payload.property ?? "",
        extra: payload.extra ?? {},
        errors: payload.errors,
      };
      throw apiError;
    }
  }

  async getPublic<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: "GET",
        ...options,
      },
      true
    );
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "GET" });
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    options: RequestInit = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async postPublic<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: "POST",
        body: data ? JSON.stringify(data) : undefined,
      },
      true
    );
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }

  async postForm<T>(endpoint: string, formData: FormData): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: formData,
      headers: {},
    });
  }

  async putForm<T>(endpoint: string, formData: FormData): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: formData,
      headers: {},
    });
  }

  async download(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.fetchWithAuth(endpoint, options);

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();

    const disposition = response.headers.get("content-disposition");

    let filename = "download.csv";
    if (disposition) {
      const filenameRegex = /filename\*?=(?:UTF-8''|")?([^";]+)/i;
      const match = disposition.match(filenameRegex);
      if (match?.[1]) {
        filename = decodeURIComponent(match[1]);
      }
    }

    return { blob, filename };
  }
}

export const apiClient = new ApiClient();
